// src/auth/nuvraAuth.ts

/**
 * Nuvra Auth (client) - Nuvra owns the email signup/login flow.
 *
 * The client calls Nuvra's own /api/auth/* endpoints (device-aware sessions, MFA,
 * audit, and Nuvra-branded auth flows instead of direct UI ownership of Supabase auth) instead of supabase.auth.signUp /
 * signInWithPassword. The server returns the Supabase session, which we install
 * with supabase.auth.setSession so the rest of the app (which authenticates API
 * calls with the Supabase token) keeps working. Supabase is the invisible
 * identity/session backend; the experience and control are Nuvra's.
 */

import { supabase } from "@/integrations/supabase/client";

declare const __API_URL__: string | undefined;
const API_BASE = (typeof __API_URL__ === "string" && __API_URL__) ? __API_URL__.replace(/\/$/, "") : "";

interface ApiResponse { ok: boolean; status: number; data?: any; error?: { code?: string; message?: string } }

async function post(path: string, body: unknown): Promise<ApiResponse> {
  try {
    const res = await fetch(`${API_BASE}/api/auth${path}`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data: json?.data, error: json?.error };
  } catch {
    return { ok: false, status: 0, error: { message: "Network error. Please try again." } };
  }
}

/** Install the Supabase session the server returned. */
async function installSession(data: any): Promise<boolean> {
  if (data?.supabaseAccessToken && data?.supabaseRefreshToken) {
    const { error } = await supabase.auth.setSession({
      access_token:  data.supabaseAccessToken,
      refresh_token: data.supabaseRefreshToken,
    });
    return !error;
  }
  return false;
}

export interface AuthOutcome {
  success: boolean;
  error?: string;
  /** Set when the account requires a second factor; complete with verifyMfa(). */
  mfaRequired?: boolean;
  mfaChallengeId?: string;
  userId?: string;
  /** How the second step verifies: authenticator ("totp") or emailed code ("email_otp"). */
  mfaMethod?: string;
  /** Why an email step-up fired: "org_policy" or "suspicious_login". */
  stepUpReason?: string;
  /** The user's organization mandates MFA and this account has not enrolled yet. */
  mfaEnrollmentRequired?: boolean;
  /** Set when signup created the account but email confirmation is required before sign-in. */
  requiresEmailVerification?: boolean;
}

export async function register(params: { email: string; password: string; displayName?: string }): Promise<AuthOutcome> {
  const r = await post("/register", params);
  if (!r.ok) return { success: false, error: r.error?.message ?? "Sign up failed" };
  if (r.data?.requiresEmailVerification) return { success: true, requiresEmailVerification: true, userId: r.data.userId };
  if (!(await installSession(r.data))) return { success: false, error: "Account created, but sign-in failed. Try logging in." };
  return { success: true, userId: r.data?.userId };
}

export async function login(params: { email: string; password: string }): Promise<AuthOutcome> {
  const r = await post("/login", params);
  if (!r.ok) return { success: false, error: r.error?.message ?? "Invalid email or password" };
  if (r.data?.mfaRequired) {
    return {
      success: false,
      mfaRequired: true,
      mfaChallengeId: r.data.mfaChallengeId,
      userId: r.data.userId,
      mfaMethod: r.data.mfaMethod ?? undefined,
      stepUpReason: r.data.stepUpReason ?? undefined,
      mfaEnrollmentRequired: r.data.mfaEnrollmentRequired === true,
    };
  }
  if (!(await installSession(r.data))) return { success: false, error: "Could not establish session" };
  return { success: true };
}

export async function verifyMfa(params: { userId: string; challengeId: string; code: string }): Promise<AuthOutcome> {
  const r = await post("/mfa/verify", { userId: params.userId, challengeId: params.challengeId, code: params.code });
  if (!r.ok) return { success: false, error: r.error?.message ?? "Verification failed" };
  if (!(await installSession(r.data))) return { success: false, error: "Could not establish session" };
  return { success: true };
}

/**
 * Request a passwordless sign-in link. Enumeration-safe: the server always
 * reports success for a well-formed request, so the UI shows "check your email"
 * regardless of whether the address maps to an account.
 */
export async function sendMagicLink(params: { email: string }): Promise<AuthOutcome> {
  const r = await post("/magic-link/send", params);
  if (!r.ok) return { success: false, error: r.error?.message ?? "Could not send the sign-in link" };
  return { success: true };
}

/**
 * Complete passwordless sign-in by claiming the one-time token from the emailed
 * link, then install the Supabase session the server mints for it.
 */
export async function verifyMagicLink(params: { token: string }): Promise<AuthOutcome> {
  const r = await post("/magic-link/verify", params);
  if (!r.ok) return { success: false, error: r.error?.message ?? "This sign-in link is invalid or has expired" };
  if (!(await installSession(r.data))) return { success: false, error: "Could not establish session" };
  return { success: true };
}

export const nuvraAuth = { register, login, verifyMfa, sendMagicLink, verifyMagicLink };
